import type { ComponentType, ReactNode } from 'react';
import type { WegicTagName } from '@/const/message';
import {
  INTERNAL_TAGS,
  WEGIC_TAGS,
} from '@/const/message';

/**
 * 组件节点数据结构
 */
export interface ComponentNode {
  type: 'component';
  tagName: string;
  attributes: Record<string, string>;
}

export interface InlineComponentProps {
  /** 组件节点数据 */
  node: ComponentNode;
}

export interface CustomTagComponentProps {
  children?: ReactNode;
  node?: unknown;
  className?: string;
}

export { WEGIC_TAGS };
export type { WegicTagName };

export { INTERNAL_TAGS };

/**
 * 自定义标签配置项
 */
export interface CustomTagConfig {
  /** 标签名（连字符格式，如 'gen-start'） */
  tag: WegicTagName;
  /** 允许的属性列表 */
  allowedAttributes?: string[];
  /** 对应的 React 组件 */
  component: ComponentType<CustomTagComponentProps>;
}

/**
 * 检查内容是否包含指定标签（兼容中线和下划线格式）
 * @param content 要检查的内容
 * @param tag 标签名
 * @returns 是否包含该标签
 */
export function hasWegicTag(content: string, tag: WegicTagName): boolean {
  const hyphenTag = tag;
  const underscoreTag = tag.replace(/-/g, '_');
  return content.includes(`<${hyphenTag}`) || content.includes(`<${underscoreTag}`);
}

/**
 * 创建 Wegic 标签
 * @param tag 标签名
 * @param content 标签内容
 * @returns 完整的标签字符串
 */
export function createWegicTag(tag: WegicTagName, content: string): string {
  return `<${tag}>${content}</${tag}>`;
}

/**
 * 从 children 中提取文本内容
 */
export function extractTextFromChildren(children: ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  if (children && typeof children === 'object' && 'props' in children) {
    return extractTextFromChildren((children as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}
